import { createSlice } from '@reduxjs/toolkit';

import {
    signInUser,
    isAuth,
    signOut,
    registerUser,
    setupTenantProfile,
    fetchUserTenants,
    updateUserProfile,
    updateUserUsername,
    updateUserEmail,
    updateUserPassword,
} from '../actions/users';
import { setActiveTenantId, getActiveTenantId } from '../../utils/tools';

let DEFAULT_USER_STATE = {
    loading: false,
    data: {
        _id: null,
        email: null,
        name: null,
        surname: null,
        type: null,
        role: null,
        verified: null,
        invitations: [],
        tenantList: [],
        activeTenantId: null,
        activeTenantBlocked: false,
        tenantListLoaded: false,
    },
    auth:null,
}

export const usersSlice = createSlice({
    name: 'users',
    initialState: DEFAULT_USER_STATE,
    reducers: {
        setVerify: (state) => {
            state.data.verified = true;
        },
        setTenantReady: (state) => {
            if (!state.data.tenant) state.data.tenant = [];
            if (state.data.tenant.length === 0) state.data.tenant.push('pending');
        },
        setActiveTenant: (state, action) => {
            state.data.activeTenantId = action.payload;
            setActiveTenantId(action.payload);
            const tenant = state.data.tenantList.find(t => t._id === action.payload);
            state.data.activeTenantBlocked = tenant?.blocked ?? false;
        }
    },
    // Its async action so we need to use extraReducers
    extraReducers:(builder) => {
        builder

        // Register User
        .addCase(registerUser.pending, (state) => {state.loading = true})
        .addCase(registerUser.fulfilled, (state,action)=> {
            state.loading = false;
            state.data = action.payload.data;
            state.auth = action.payload.auth;
        })
        .addCase(registerUser.rejected, (state)=> { state.loading = false })
        
         // SIGN IN
         .addCase(signInUser.pending,(state)=>{ state.loading = true })
         .addCase(signInUser.fulfilled,(state,action)=>{
             state.loading = false;
             state.data = action.payload.data;
             state.auth = action.payload.auth;
         })
         .addCase(signInUser.rejected,(state)=>{ state.loading = false })
          
         // IS AUTH
          .addCase(isAuth.pending,(state)=>{ state.loading = true })
          .addCase(isAuth.fulfilled,(state,action)=>{
              state.loading = false;
              state.data = { ...state.data,...action.payload.data}
              state.auth = action.payload.auth;
          })
          .addCase(isAuth.rejected,(state)=>{ state.loading = false })
        
        // SETUP TENANT
        .addCase(setupTenantProfile.pending, (state) => { state.loading = true })
        .addCase(setupTenantProfile.fulfilled, (state, action) => {
            state.loading = false;
            state.data.createdTenantId = action.payload?._id ?? null;
            if (!state.data.tenant) state.data.tenant = [];
            if (state.data.tenant.length === 0) state.data.tenant.push('ready');
        })
        .addCase(setupTenantProfile.rejected, (state) => { state.loading = false })

        // FETCH USER TENANTS
        .addCase(fetchUserTenants.fulfilled, (state, action) => {
            state.data.tenantList = action.payload;
            state.data.tenantListLoaded = true;
            if (action.payload.length > 0) {
                const stored = getActiveTenantId();
                const valid = action.payload.find(t => t._id === stored);
                const resolved = valid ? stored : action.payload[0]._id;
                state.data.activeTenantId = resolved;
                setActiveTenantId(resolved);
                const activeTenant = action.payload.find(t => t._id === resolved);
                state.data.activeTenantBlocked = activeTenant?.blocked ?? false;
            }
        })

        // UPDATE OWN PROFILE
        .addCase(updateUserProfile.fulfilled, (state, action) => {
            state.data = { ...state.data, ...action.payload };
        })

        // UPDATE OWN USERNAME
        .addCase(updateUserUsername.fulfilled, (state, action) => {
            state.data = { ...state.data, ...action.payload };
        })

        // UPDATE OWN EMAIL
        .addCase(updateUserEmail.fulfilled, (state, action) => {
            state.data = { ...state.data, ...action.payload };
        })

        // UPDATE OWN PASSWORD
        .addCase(updateUserPassword.fulfilled, (state, action) => {
            state.data = { ...state.data, ...action.payload };
        })

        // SIGN OUT
        .addCase(signOut.fulfilled, (state, action) => {
            state.data = DEFAULT_USER_STATE.data;
            state.auth = false;
        })
        .addCase(signOut.rejected,(state)=>{ state.loading = false })
    }
});

export const { setVerify, setTenantReady, setActiveTenant } = usersSlice.actions;
export default usersSlice.reducer
